import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export type ItemRefs = Record<string, HTMLDivElement | null>;

const useScrollTracking = (
  itemRefs?: RefObject<ItemRefs>,
  scrollRef?: RefObject<HTMLElement | null>, // Optional scrollRef parameter
  scrollOffset?: number, // Optional scroll offset parameter
): {
  activeItemId: string | null;
  setActiveItemId: (itemId: string | null) => void;
  scrollToItem: (itemId: string) => void;
} => {
  const lastScrollTopEdgeY = useRef(0);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  const isSmoothScrolling = useRef(false);

  // On load set the active item to the first section
  useEffect(() => {
    if (activeItemId) {
      return;
    }

    const timeout = setTimeout(() => {
      const scrollContainer = scrollRef ? scrollRef.current : window;
      const isWindow = scrollContainer === window;
      const scrollTopEdgeY =
        isWindow ?
          parseInt(window.pageYOffset.toFixed(), 10)
        : (scrollContainer as HTMLElement).scrollTop;

      if (itemRefs?.current && Object.keys(itemRefs.current).length > 0) {
        const itemsWithRect: Array<{ itemId: string; itemTopEdge: number }> =
          [];

        for (const itemId of Object.keys(itemRefs.current)) {
          const itemRef = itemRefs.current[itemId];
          if (!itemRef) {
            continue; // Skip if the ref is undefined
          }
          const clientRect = itemRef.getBoundingClientRect();
          itemsWithRect.push({
            itemId,
            itemTopEdge: scrollTopEdgeY + clientRect.top,
          });
        }

        if (itemsWithRect.length === 0) {
          return;
        }

        itemsWithRect.sort((a, b) => {
          return a.itemTopEdge - b.itemTopEdge;
        });

        setActiveItemId(itemsWithRect[0].itemId);
        clearTimeout(timeout);
      }
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [activeItemId, itemRefs, scrollRef]);

  const getScrollContainer = useCallback(() => {
    // Use scrollRef if provided, or fallback to window
    return scrollRef ? scrollRef.current : window;
  }, [scrollRef]);

  const scrollToItem = useCallback(
    (itemId: string) => {
      if (!itemRefs || isSmoothScrolling.current) {
        return;
      }

      const itemRef = itemRefs.current?.[itemId];
      if (!itemRef) {
        return;
      }

      const scrollContainer = getScrollContainer();
      const isWindow = scrollContainer === window;
      const scrollTopEdgeY =
        isWindow ?
          parseInt(window.pageYOffset.toFixed(), 10)
        : (scrollContainer as HTMLElement).scrollTop;

      const clientRect = itemRef.getBoundingClientRect();

      isSmoothScrolling.current = true;
      setActiveItemId(itemId);

      // There is no direct callback to "scrollTo", instead we subscribe to updates to the scrolling functions until it stops moving. Within this we lock the scroll events so that the tracking does not get confused
      const clearScrollTimeout = () => {
        scrollContainer?.removeEventListener("scroll", scrollTracker);
        isSmoothScrolling.current = false;
      };
      let timeout = setTimeout(() => {
        clearScrollTimeout();
      }, 100);
      const scrollTracker = (e) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          clearScrollTimeout();
        }, 100);
      };
      scrollContainer?.addEventListener("scroll", scrollTracker);

      scrollContainer?.scrollTo({
        top: scrollTopEdgeY + clientRect.top + (scrollOffset || 0),
        left: 0,
        behavior: "smooth",
      });
    },
    [getScrollContainer, scrollOffset, itemRefs],
  );

  useEffect(() => {
    const onScroll = () => {
      if (isSmoothScrolling.current) {
        return;
      }

      const scrollContainer = getScrollContainer();
      if (!scrollContainer) {
        return;
      }
      if (!itemRefs?.current) {
        return;
      }

      const isWindow = scrollContainer === window;
      const windowHeight =
        isWindow ?
          window.innerHeight
        : (scrollContainer as HTMLElement).clientHeight;

      const scrollTopEdgeY =
        isWindow ?
          parseInt(window.pageYOffset.toFixed(), 10)
        : (scrollContainer as HTMLElement).scrollTop;
      const scrollBottomEdgeY = scrollTopEdgeY + windowHeight;

      if (scrollTopEdgeY === lastScrollTopEdgeY.current) {
        return;
      }

      const direction =
        scrollTopEdgeY > lastScrollTopEdgeY.current ? "down" : "up";
      lastScrollTopEdgeY.current = scrollTopEdgeY;

      const itemsWithRect: Array<{
        itemId: string;
        itemTopEdge: number;
        itemBottomEdge: number;
        ref: HTMLElement;
      }> = [];

      for (const itemId of Object.keys(itemRefs.current)) {
        const itemRef = itemRefs.current[itemId];
        if (!itemRef) {
          continue; // Skip if the ref is undefined
        }
        const clientRect = itemRef.getBoundingClientRect();
        itemsWithRect.push({
          itemId,
          itemTopEdge: clientRect.top + scrollTopEdgeY,
          itemBottomEdge: clientRect.bottom + scrollTopEdgeY,
          ref: itemRef,
        });
      }

      if (itemsWithRect.length === 0) {
        return;
      }

      // Sort by layout, reversed when scrolling up to allow early return
      itemsWithRect.sort((a, b) => {
        if (direction === "down") {
          return b.itemTopEdge - a.itemTopEdge;
        }
        return a.itemTopEdge - b.itemTopEdge;
      });

      const dist = windowHeight / 2 - 100;
      for (const item of itemsWithRect) {
        const { itemId, itemTopEdge, itemBottomEdge } = item;

        if (direction === "down" && scrollBottomEdgeY - dist >= itemTopEdge) {
          setActiveItemId(itemId);
          return;
        } else if (
          direction === "up" &&
          scrollTopEdgeY + dist <= itemBottomEdge
        ) {
          setActiveItemId(itemId);
          return;
        }
      }
      setActiveItemId(itemsWithRect[0].itemId);
    };

    const scrollContainer = getScrollContainer();
    scrollContainer?.addEventListener("scroll", onScroll);

    return () => {
      scrollContainer?.removeEventListener("scroll", onScroll);
    };
  }, [itemRefs, getScrollContainer]);

  return useMemo(
    () => ({ activeItemId, setActiveItemId, scrollToItem }),
    [activeItemId, scrollToItem],
  );
};

export default useScrollTracking;
